import React from 'react'

import * as c from '../../../common'
import {
  TOTAL_PENDING_LABEL,
  totalCostPendingApply,
} from '../../../common/helpers/totalCostDisplay'
import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import * as cp from '../../comps'
import * as gs from '../../../app/gStyles'

import { firstElectricContract } from '../../constants'

import ContractLogo, {
  ContractLogoOtherFields,
  useContractWatermark,
} from './ContractLogo'
import ReportPages, { horizontalPad } from './ReportPages'
//@ts-ignore
import styles from './SecurityContract.module.css'
import { DetailHeader, DetailLine } from './Detail'

type Clause = { title: string; body: string }

export type FirstElectricContractProps = c.r<{
  customerID: string
  isPDF: boolean
  readOnly?: boolean
  toggleSecuritySignatureModalCompany(): void
  toggleSecuritySignatureModalCustomer(): void
  toggleCancelSignCustomer(): void
  clauses?: readonly Clause[]
  showAdditionalPages?: boolean
  alarmSafe?: boolean
}>

export default React.memo<FirstElectricContractProps>(
  function FirstElectricContract({
    customerID,
    isPDF,
    readOnly,
    toggleSecuritySignatureModalCompany,
    toggleSecuritySignatureModalCustomer,
    toggleCancelSignCustomer,
    clauses = firstElectricContract,
    showAdditionalPages = true,
    alarmSafe = false,
  }) {
    //#region styling
    //#endregion
    //#region global
    const { contractCompanyFull } = r.useContractCompany(customerID)

    // 2026-07-30: added a CCTV-only wording swap here (mirroring
    // HomeImprovementAgreement.tsx). Retired 2026-08-31 — Jaron, #development-dpt
    // 12:45: "I want it to say Home Improvement for everything including CCTV".
    // The T&C body always reads "home improvement" now.
    const systemTypeUpper = 'HOME IMPROVEMENT'
    const systemTypeLower = 'home improvement'

    // Clauses reference the company by name via {{COMPANY_NAME}} (see
    // constants.ts doc comment) — never hardcoded, so this same
    // FirstElectricContract renders correctly for MSE, "Other (Financing)",
    // and any future clause set reusing the token.
    const resolveClauseBody = React.useCallback(
      (body: string): string =>
        body
          .replace(/\{\{COMPANY_NAME\}\}/g, contractCompanyFull.longName)
          .replace(/HOME IMPROVEMENT/g, systemTypeUpper)
          .replace(/home improvement/g, systemTypeLower),
      [contractCompanyFull.longName, systemTypeLower, systemTypeUpper],
    )

    const totalCost = r.useTotalCost('customer', customerID)

    const { appliedLoan } = r.usePayment(customerID)

    const monthlyPayment = React.useMemo((): number => {
      if (!appliedLoan) return 0
      const { grandTotalWDF, paymentMethod, termInterest, termYears } =
        appliedLoan
      // Cash customers have no loan payment, and their stored termYears may
      // be 0 (division by zero → Infinity) or stale from a prior financing
      // selection — mirror Payment.tsx, which only shows $X/mo for financing.
      if (paymentMethod === 'cash' || termYears <= 0) return 0
      if (termInterest === 0) {
        return grandTotalWDF / termYears / 12
      }
      return c.calcLoanMonthlyPay(grandTotalWDF, termInterest * 100, termYears)
    }, [appliedLoan])

    const [customerData] = r.useCustomer(customerID)
    const totalPending = totalCostPendingApply(customerData, c.IS_MAIN)

    const {
      customerAddress,
      customerEmail,
      customerName,
      customerPhone,
      homeRep,

      security_contract_sign_company,
      security_contract_sign_company_date,
      security_contract_sign_customer,
      security_contract_sign_customer_date,
      security_contract_cancel_sign_customer,
      security_contract_cancel_sign_customer_date,
      security_monitoring_enabled,
      solarCompany,
    } = customerData

    // Any signature on this document freezes how it looks (V67): no
    // watermark once the customer or the company signed anything here.
    const watermark = useContractWatermark(
      customerID,
      !!(
        security_contract_sign_customer ||
        security_contract_sign_company ||
        security_contract_cancel_sign_customer
      ),
    )

    // Both hooks called unconditionally (rules of hooks) — alarmSafe picks
    // which result to use, same pattern as CertOfCompletion.tsx (T59).
    const unrestrictedDetail = r.useDetailLines(customerID)
    const alarmSafeDetail = r.useSecurityScopeDetailLines(customerID)
    const {
      collapsed,
      collapsedHandleMax,
      collapsedHandleMin,
      detailLines: _detailLines,
    } = alarmSafe ? alarmSafeDetail : unrestrictedDetail

    // The last line renders inside the `flowRoot` below so it cannot be split
    // from the TOTAL across a page break — that is the only reason it is
    // pulled out of the list.
    //
    // Guarding on `length > 1` meant a scope that resolved to exactly ONE line
    // produced neither: `detailLines` was empty AND `lastDetailLine` was
    // undefined, so the SCOPE OF WORK printed its column headings, nothing at
    // all, then a TOTAL charging the full price. `slice(0, -1)` is already []
    // at length 1 and at length 0, so the length test bought nothing and cost
    // the single-line case.
    const detailLines = _detailLines.slice(0, -1)
    const lastDetailLine = _detailLines[_detailLines.length - 1]

    const [coBuyer, , coBuyerHandle] = r.useCustomerFieldInput(
      customerID,
      'security_contract_co_buyer',
    )
    //#endregion
    const buttonSignatureStyle = React.useMemo(
      () => ({
        backgroundColor: c.companyColors[solarCompany],
        '&:hover': { backgroundColor: c.companyColors[solarCompany] },
        width: 256,
      }),
      [solarCompany],
    )

    const companyState = c.getCompanyState(solarCompany)
    const companyLongName =
      contractCompanyFull.longNames?.[companyState] ??
      contractCompanyFull.longName

    // withLogo clears the absolutely-positioned ContractLogo (200px wide)
    // to its left - only the very first page of the contract renders that
    // logo, so the other pages that reuse this same header block must NOT
    // get the extra left margin (it'd leave a pointless gap with nothing
    // to clear).
    const renderHeader = (withLogo: boolean) => (
      <div style={withLogo ? sx.headerText : undefined}>
        <p
          className={styles['heading']}
          style={withLogo ? sx.headerHeading : undefined}
        >
          {companyLongName}
        </p>

        <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
          {contractCompanyFull.address1s?.[companyState] ??
            contractCompanyFull.address1}
        </p>
        <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
          {contractCompanyFull.address2s?.[companyState] ??
            contractCompanyFull.address2}
        </p>
        <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
          {contractCompanyFull.email}
        </p>
        <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
          License #:
          {contractCompanyFull.roofingLicenses?.[companyState] ??
            contractCompanyFull.roofingLicense}
        </p>
        <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
          {contractCompanyFull.phoneNumbers?.[companyState] ??
            contractCompanyFull.phoneNumber}
        </p>
        {contractCompanyFull.website && (
          <p style={withLogo ? sx.companyInfo : sx.companyInfoCentered}>
            {contractCompanyFull.website}
          </p>
        )}
      </div>
    )
    const header = renderHeader(true)
    const headerNoLogo = renderHeader(false)

    return (
      <>
        <ReportPages
          companyBorder
          customerID={customerID}
          hPad
          watermark={watermark}
        >
          <div style={sx.logo}>
            <ContractLogo isPDF={isPDF} readOnly={readOnly} size="l" />
          </div>

          <ContractLogoOtherFields
            disabled={
              readOnly ||
              !!security_contract_sign_customer ||
              !!security_contract_sign_company
            }
            isPDF={isPDF}
          />

          {gapBig}

          {header}

          {gapMedium}

          <p style={sx.node}>
            This is a Purchase Agreement ("PA") on this Date{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {security_contract_sign_customer || security_contract_sign_company
                ? c.formatContractDate(
                    security_contract_sign_customer_date ||
                      security_contract_sign_company_date,
                    solarCompany,
                  )
                : c.formatContractDate(Date.now(), solarCompany)}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            between {companyLongName} ("Company") and:
          </p>

          {gapMedium}

          <p style={sx.node}>
            Buyer:{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerName}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            Co-Buyer:{' '}
            <input
              className={styles['input-inline-wide']}
              disabled={
                readOnly ||
                !!security_contract_sign_customer ||
                !!security_contract_sign_company
              }
              onChange={coBuyerHandle}
              title="Co-Buyer"
              value={coBuyer}
            />
          </p>

          <p style={sx.node}>
            Property Address:{' '}
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerAddress}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>
          </p>

          <p style={sx.node}>
            Home / Cell Home:
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {customerPhone}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            E-mail:
            <span className={styles['underlined']}>
              {c.blankChar(8)}
              {customerEmail}
              {c.blankChar(8)}
            </span>
          </p>

          <p style={sx.node}>
            sometimes collectively referred to herein as the "Parties or
            individually a "Party".
          </p>

          {gapSmall}

          <p style={sx.node}>
            Rep Name:
            <span className={styles['underlined']}>
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              {c.displayHomeRepOnContract(homeRep)}
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            </span>{' '}
            Rep Phone:
            <span className={styles['underlined']}>
              {c.blankChar(8)}
              n/a
              {c.blankChar(8)}
            </span>
          </p>

          {gapMedium}

          <p className={styles['subheading']}>SCOPE OF WORK</p>

          {gapMedium}

          <DetailHeader isPDF={isPDF} />

          {detailLines.map(({ id, name, notes, price, qty }) => (
            <DetailLine
              collapsed={!!collapsed[id]}
              id={id}
              isPDF={isPDF}
              name={name}
              notes={notes}
              onCollapse={collapsedHandleMin[id]!}
              onExpand={collapsedHandleMax[id]!}
              price={price}
              qty={qty}
              readOnly={readOnly}
            />
          ))}

          <div style={gs.flowRootWidth100}>
            {lastDetailLine && (
              <DetailLine
                collapsed={!!collapsed[lastDetailLine.id]}
                id={lastDetailLine.id}
                isPDF={isPDF}
                name={lastDetailLine.name}
                notes={lastDetailLine.notes}
                price={lastDetailLine.price}
                qty={lastDetailLine.qty}
                onCollapse={collapsedHandleMin[lastDetailLine.id]!}
                onExpand={collapsedHandleMax[lastDetailLine.id]!}
                readOnly={readOnly}
              />
            )}

            {gapSmall}

            <p style={sx.grandTotal}>
              <span style={sx.bold}>
                TOTAL: {c.blankChar(12)}
                {totalPending
                  ? TOTAL_PENDING_LABEL
                  : Intl.NumberFormat('en-US', {
                      currency: 'USD',
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      style: 'currency',
                    }).format(totalCost)}
              </span>
            </p>

            {monthlyPayment > 0 && (
              <p style={sx.grandTotal}>
                Monthly Payment: {c.blankChar(4)}
                {Intl.NumberFormat('en-US', {
                  currency: 'USD',
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                  style: 'currency',
                }).format(monthlyPayment)}
                /mo
              </p>
            )}

            {security_monitoring_enabled === 'yes' && !(isPDF && alarmSafe) && (
              <p style={sx.grandTotal}>
                Monitoring service: $9.99/mo (billed monthly, not included in
                total)
              </p>
            )}

            {gapMedium}
          </div>

          {clauses.slice(0, -1).map(({ body, title }, i) => (
            <React.Fragment key={i}>
              <p style={sx.node}>
                <span style={sx.bold}>{title && title + '. '}</span>
                {resolveClauseBody(body)}.
              </p>

              {gapSmall}
            </React.Fragment>
          ))}

          {/* B54/V47: the last clause is bundled into the SAME flow-root as
              the signature block below (not left as its own top-level
              ReportPages child) so the chunker either fits both on the
              current page or moves both together to the next one - avoids
              this clause getting stranded alone with the signature block
              pushed to its own near-empty page (same pattern as T41 round 4,
              HomeImprovementAgreement.tsx). */}
          <div style={gs.flowRootWidth100}>
            {clauses.length > 0 && (
              <>
                <p style={sx.node}>
                  <span style={sx.bold}>
                    {clauses[clauses.length - 1]!.title &&
                      clauses[clauses.length - 1]!.title + '. '}
                  </span>
                  {resolveClauseBody(clauses[clauses.length - 1]!.body)}.
                </p>

                {gapSmall}
              </>
            )}

            <div className={styles['signatures']}>
              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Property owner:</p>

                  <span>{customerName}</span>
                </div>

                {gs.rowGap24}

                {security_contract_sign_customer && (
                  <>
                    <img
                      alt=""
                      className={styles['signature-img']}
                      src={security_contract_sign_customer}
                      width={200}
                    />
                    <p>
                      {c.formatContractDate(
                        security_contract_sign_customer_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                )}

                <p>Address: {customerAddress}</p>

                {gs.rowGap24}

                {isPDF || security_contract_sign_customer ? (
                  <div />
                ) : (
                  <Button
                    data-guided-signature="security_contract_sign_customer"
                    onClick={toggleSecuritySignatureModalCustomer}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Client Signature
                  </Button>
                )}
              </div>

              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Contractor:</p>
                  <span>{c.displayHomeRepOnContract(homeRep)}</span>
                </div>

                {security_contract_sign_company && (
                  <>
                    <img
                      alt=""
                      className={styles['signature-img']}
                      src={security_contract_sign_company}
                      width={200}
                    />
                    <p>
                      {c.formatContractDate(
                        security_contract_sign_company_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                )}

                {gs.rowGap24}

                <p className={styles['label-signature']}>
                  Authorized Representative of
                </p>
                <p className={styles['label-signature']}>{companyLongName}</p>

                {gs.rowGap24}

                {isPDF || security_contract_sign_company ? (
                  <div />
                ) : (
                  <Button
                    onClick={toggleSecuritySignatureModalCompany}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Authorized Signature
                  </Button>
                )}
              </div>
            </div>
          </div>
        </ReportPages>

        <cp.PageReport
          company={solarCompany}
          companyBorder
          hPad
          watermark={watermark}
        >
          {headerNoLogo}
          {gapMedium}
          <p className={styles['subheading']}>Notice To Buyer</p>
          {gapSmall}
          <p style={sx.node}>
            You are entering into a Purchase Agreement, to purchase certain
            energy efficient improvements
          </p>
          {gapSmall}
          <p style={sx.node}>
            Notice to the Buyer (do not sign this Purchase Agreement before you
            read it & all its attachments or if it contains blank space), you
            will be sent a copy of the agreement you sign. You, the Buyer(s),
            may cancel this Purchase Agreement at any time prior to midnight of
            the third business day after the date of this transaction without a
            penalty.
          </p>
          {gapSmall}
          <p style={sx.node}>
            If the project includes solar a cancellation fee of $1,750.00 will
            be charged to you if you cancel after the 3rd business day. After a
            period of 7 days or more the cancellation fee will increase and
            include: the costs of pictures & measurements / solar engineering
            work completed / any additional engineering for ground-mount site
            visit / permit fees / utility interconnection fees / homeowner's
            association fees / materials shipping & restocking fees / attorney
            fees / lender & financing fees / project management & admin fees /
            engineering stamp fees / any other fees the Company accrued.
          </p>
          {gapSmall}
          <p style={sx.node}>
            Under the right to rescind, you have until midnight of the third
            business day to cancel the Purchase Agreement. Day one begins after
            this occurs:
          </p>
          {gapSmall}
          <p style={sx.node}>- YOU SIGN THE PURCHASE AGREEMENT</p>
          {gapSmall}
          <p style={sx.node}>
            - YOU RECEIVE A TRUTH IN LENDING DISCLOSURE FORM CONTAINING CERTAIN
            KEY INFORMATION ABOUT YOUR CREDIT CONTRACT, INCLUDING THE ANNUAL
            PERCENTAGE RATE; AMOUNT FINANCED AND PAYMENT SCHEDULE.
          </p>
          {gapSmall}
          <p style={sx.node}>
            If you decide to rescind, you must notify {companyLongName} in
            writing. You may not rescind by telephone or by a face-to-face
            conversation with your sales representative. Your written notice
            must be mailed, or delivered by other written means, before midnight
            of the third business day to the Company's Corporate Office{' '}
            {contractCompanyFull.address1 + ' ' + contractCompanyFull.address2}.
          </p>
        </cp.PageReport>

        {/* Own auto-paginated block (not part of the fixed-height PageReport
            above) — the signature button (hidden in PDF, visible in the live
            web view) can push this section's height past a single A4 page,
            same fix pattern as HomeImprovementAgreement's Notice of
            Cancellation block (B54/T41). */}
        <ReportPages
          companyBorder
          customerID={customerID}
          hPad
          watermark={watermark}
        >
          {headerNoLogo}
          {gapMedium}
          <p className={styles['subheading']}>NOTICE OF CANCELLATION</p>
          {gapSmall}
          <p style={sx.bold}>
            DATE OF TRANSACTION:{' '}
            <span className={styles['underlined']}>{c.blankChar(64)}</span>
          </p>
          {gapSmall}
          <p style={sx.node}>
            YOU MAY CANCEL THIS TRANSACTION, WITHOUT ANY PENALTY OR OBLIGATION,
            WITHIN THREE BUSINESS DAYS FROM THE ABOVE DATE. IF YOU CANCEL:
          </p>
          <p style={sx.node}>
            1. ANY PROPERTY TRADED IN, ANY PAYMENTS MADE BY YOU UNDER THE
            CONTRACT OR SALE, AND ANY NEGOTIABLE INSTRUMENT EXECUTED BY YOU WILL
            BE RETURNED WITHIN 10 BUSINESS DAYS FOLLOWING RECEIPT BY THE COMPANY
            OF YOUR CANCELLATION NOTICE, AND ANY SECURITY INTEREST ARISING OUT
            OF THE TRANSACTION WILL BE CANCELED.
          </p>
          <p style={sx.node}>
            2. YOU MUST MAKE AVAILABLE TO THE COMPANY AT YOUR PROPERTY, IN
            SUBSTANTIALLY AS GOOD CONDITION AS WHEN RECEIVED, ANY GOODS
            DELIVERED TO YOU UNDER THIS PURCHASE AGREEMENT OR SALE; OR YOU MAY
            IF YOU WISH, COMPLY WITH THE INSTRUCTIONS OF THE COMPANY REGARDING
            THE RETURN SHIPMENT OF THE PRODUCTS AT THE COMPANY'S EXPENSE AND
            RISK.
          </p>
          <p style={sx.node}>
            3. IF THE COMPANY DOES NOT PICK UP THE DELIVERED GOODS UNDER THIS
            PURCHASE AGREEMENT WITHIN 10 DAYS OF THE DATE OF YOUR NOTICE OF
            CANCELLATION, YOU MAY RETAIN OR DISPOSE OF THE GOODS WITHOUT ANY
            FURTHER OBLIGATION.
          </p>
          <p style={sx.node}>
            4. TO CANCEL THIS TRANSACTION, MAIL OR DELIVER A SIGNED AND DATED
            COPY OF THIS CANCELLATION NOTICE OR ANY OTHER WRITTEN NOTICE, TO{' '}
            {companyLongName}{' '}
            {contractCompanyFull.address1 + ' ' + contractCompanyFull.address2}.
          </p>
          {gapSmall}
          <p style={sx.node}>
            NOT LATER THAN MIDNIGHT OF THIS DATE:{' '}
            <span className={styles['underlined']}>{c.blankChar(128)}</span>
          </p>
          {gapSmall}

          {/* Bundled together so ReportPages' auto-chunker either fits both
              on the current page or moves both to the next one - avoids "I
              HEREBY CANCEL" text stranded alone with the signature block
              pushed to its own near-empty page (same pattern as
              HomeImprovementAgreement). */}
          <div style={gs.flowRootWidth100}>
            <p style={sx.node}>I HEREBY CANCEL THIS PURCHASE AGREEMENT</p>
            {gapMedium}
            <div className={styles['signatures']}>
              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Buyer Signature:</p>
                  <span>{customerName}</span>
                </div>
                {gs.rowGap24}
                {security_contract_cancel_sign_customer && (
                  <>
                    <img
                      alt=""
                      className={styles['signature-img']}
                      src={security_contract_cancel_sign_customer}
                      width={200}
                    />
                    <p>
                      {c.formatContractDate(
                        security_contract_cancel_sign_customer_date,
                        solarCompany,
                        'MM/DD/YYYY - hh:mma',
                      )}
                    </p>
                  </>
                )}
                {gs.rowGap24}
                {isPDF || security_contract_cancel_sign_customer ? (
                  <div />
                ) : (
                  <Button
                    onClick={toggleCancelSignCustomer}
                    sx={buttonSignatureStyle}
                    variant="contained"
                  >
                    Buyer Signature
                  </Button>
                )}
              </div>
              <div className={styles['signature-container']}>
                <div>
                  <p className={styles['label-signature']}>Date:</p>
                  <span>
                    {security_contract_cancel_sign_customer
                      ? c.formatContractDate(
                          security_contract_cancel_sign_customer_date,
                          solarCompany,
                        )
                      : c.formatContractDate(Date.now(), solarCompany)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ReportPages>

        {showAdditionalPages && (
          <cp.PageReport
            company={solarCompany}
            companyBorder
            hPad
            watermark={watermark}
          >
            {headerNoLogo}
            {gapMedium}
            <p className={styles['subheading']}>
              Customer Acknowledgement &amp; Addendum to Purchase Agreement
            </p>
            {gapMedium}
            <p style={sx.node}>
              <span style={sx.bold}>TAX CREDIT DEFINED</span>
            </p>
            {gapSmall}
            <p style={sx.node}>
              An Amount of money that a taxpayer is able to subtract from the
              amount of tax that they owe to the government. Unlike deductions
              and exemptions, which reduce the amount of your income that is
              taxable, tax credits reduce the actual amount of tax that you owe
              the government. A Tax credit is generally much more valuable than
              a deduction. The tax credit reduces the actual amount of tax that
              must be paid. A deduction, on the other hand, only reduces the
              taxable income. A tax credit does not depend on the tax rate and
              so it is of equal value to a taxpayer regardless of income level.
              Your initials indicate that you understand that in order to take
              full advantage of the federal government's Tax Credit, you must
              have a tax obligation that the tax credit can offset.
            </p>
            {gapMedium}
            <p style={sx.node}>
              <span style={sx.bold}>SHADING (Solar Systems)</span>
            </p>
            {gapSmall}
            <p style={sx.node}>
              I acknowledge &amp; understand that{' '}
              <span style={sx.bold}>any and all</span> shading issues is the
              sole responsibility of the homeowner/purchaser to{' '}
              <span style={sx.bold}>maintain or remove</span> any and all
              shading issues including trees. Failure to do so will impact
              system performance, rebates and utility bills.
            </p>
            {gapMedium}
            <p style={sx.node}>
              Your signature below confirms that you have read, understand, and
              agree to the foregoing.
            </p>
            {gapMedium}
            <div style={gs.flowRootWidth100}>
              <div className={styles['signatures']}>
                <div className={styles['signature-container']}>
                  <div>
                    <p className={styles['label-signature']}>Property owner:</p>
                    <span>{customerName}</span>
                  </div>
                  {gs.rowGap24}
                  {security_contract_sign_customer && (
                    <>
                      <img
                        alt=""
                        className={styles['signature-img']}
                        src={security_contract_sign_customer}
                        width={200}
                      />
                      <p>
                        {c.formatContractDate(
                          security_contract_sign_customer_date,
                          solarCompany,
                          'MM/DD/YYYY - hh:mma',
                        )}
                      </p>
                    </>
                  )}
                  <p>Address: {customerAddress}</p>
                  {gs.rowGap24}
                  {isPDF || security_contract_sign_customer ? (
                    <div />
                  ) : (
                    <Button
                      data-guided-signature="security_contract_sign_customer"
                      onClick={toggleSecuritySignatureModalCustomer}
                      sx={buttonSignatureStyle}
                      variant="contained"
                    >
                      Client Signature
                    </Button>
                  )}
                </div>
                <div className={styles['signature-container']}>
                  <div>
                    <p className={styles['label-signature']}>Date:</p>
                    <span>
                      {security_contract_sign_customer ||
                      security_contract_sign_company
                        ? c.formatContractDate(
                            security_contract_sign_customer_date ||
                              security_contract_sign_company_date,
                            solarCompany,
                          )
                        : c.formatContractDate(Date.now(), solarCompany)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </cp.PageReport>
        )}
      </>
    )
  },
)

const gapBig = gs.rowGap48
const gapMedium = gs.rowGap32
const gapSmall = gs.rowGap16

const sxNode = {
  alignSelf: 'flex-start',
  fontFamily: c.light.fontFamily,
  fontSize: 11,
  margin: 0,
}
const sx = {
  bold: { ...sxNode, fontWeight: 'bold' },
  boldCursive: { ...sxNode, fontStyle: 'italic', fontWeight: 'bold' },
  companyInfo: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'left' as const,
  },
  companyInfoCentered: {
    fontFamily: c.light.fontFamily,
    margin: 0,
    textAlign: 'center' as const,
  },
  grandTotal: { ...sxNode, textAlign: 'end' as const, width: '100%' },
  headerHeading: { textAlign: 'left' as const },
  // clears the absolutely-positioned logo (200px wide) to its left - the
  // company longName can be much longer than the original "First Electric"
  // (e.g. "WNT, LLC dba First Electric"), and centered text of that length
  // used to render underneath/behind the logo.
  headerText: { marginLeft: 220 },
  node: sxNode,
  logo: { left: horizontalPad, position: 'absolute' as const, top: '96px' },
}
